import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, ChevronDown, Menu, X, LogOut, BookOpen, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { getCart } from '../lib/cart';
import BrandLogo from './BrandLogo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showCats, setShowCats] = useState(false);
  const [cats, setCats] = useState([]);
  const [cartCount, setCartCount] = useState(getCart().length);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { api.categories().then(setCats).catch(() => {}); }, []);

  useEffect(() => {
    setCartCount(getCart().length);
    setMobileOpen(false);
    setShowCats(false);
  }, [location.pathname]);

  useEffect(() => {
    const id = setInterval(() => setCartCount(getCart().length), 1000);
    return () => clearInterval(id);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-xl shadow-[0_4px_30px_rgba(107,71,220,0.08)]' : 'bg-white/70 backdrop-blur-md'}`}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center gap-4 h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <BrandLogo size="md" showWordmark={true} wordmarkClass="text-xl" />
          </Link>

          <div className="relative hidden lg:block">
            <button
              onClick={() => setShowCats(!showCats)}
              onMouseEnter={() => setShowCats(true)}
              className="flex items-center gap-1 px-3 py-2 rounded-lg text-[15px] font-semibold text-gray-700 hover:text-[#6b47dc] hover:bg-purple-50 transition-colors"
            >
              Categories <ChevronDown size={16} className={`transition-transform ${showCats ? 'rotate-180' : ''}`} />
            </button>
            {showCats && cats.length > 0 && (
              <div
                onMouseLeave={() => setShowCats(false)}
                className="absolute top-full left-0 mt-2 w-[720px] bg-white rounded-2xl shadow-2xl border border-purple-100 p-4 grid grid-cols-3 gap-1"
              >
                {cats.map(c => (
                  <Link key={c.id} to={`/courses?category=${c.id}`} className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-purple-50 group">
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#6b47dc]">{c.name}</span>
                    <span className="text-xs text-gray-400">{c.count}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="hidden md:flex flex-1 max-w-md relative">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/courses?q=${encodeURIComponent(e.target.value)}`); }}
              placeholder="What do you want to learn?"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#6b47dc] focus:ring-2 focus:ring-purple-100 outline-none text-sm transition-all"
            />
          </div>

          <nav className="hidden lg:flex items-center gap-1 ml-auto">
            <Link to="/courses" className="px-3 py-2 text-[15px] font-semibold text-gray-700 hover:text-[#6b47dc]">Courses</Link>
            <Link to="/dod-8140-certification-training" className="px-3 py-2 text-[15px] font-semibold text-gray-700 hover:text-[#6b47dc]">DoD 8140 Training</Link>
            <Link to="/courses?type=corporate" className="px-3 py-2 text-[15px] font-semibold text-gray-700 hover:text-[#6b47dc]">For Business</Link>
            <Link to="/contact" className="px-3 py-2 text-[15px] font-semibold text-gray-700 hover:text-[#6b47dc]">Contact</Link>
          </nav>

          <div className="flex items-center gap-2 ml-auto lg:ml-0">
            <Link to="/cart" className="relative p-2 rounded-lg hover:bg-purple-50 text-gray-700 hover:text-[#6b47dc] transition-colors">
              <ShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#ff5722] text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center">{cartCount}</span>
              )}
            </Link>

            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-purple-50">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6b47dc] to-[#ff5722] grid place-items-center text-white font-bold text-sm">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <span className="hidden md:inline text-sm font-semibold text-gray-700">{user.name?.split(' ')[0]}</span>
                </button>
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-2xl border border-purple-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="p-3 border-b border-gray-100">
                    <div className="font-semibold text-sm">{user.name}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                    {isAdmin && <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded bg-[#6b47dc] text-white font-bold uppercase">{user.role.replace('_', ' ')}</div>}
                  </div>
                  <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-purple-50"><BookOpen size={16} /> My Learning</Link>
                  {isAdmin && (
                    <Link to="/admin" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-purple-50 text-[#6b47dc] font-semibold"><Shield size={16} /> Admin Panel</Link>
                  )}
                  <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-red-50 text-red-600 border-t border-gray-100"><LogOut size={16} /> Sign out</button>
                </div>
              </div>
            ) : (
              <>
                <Link to="/login" className="hidden md:inline-flex">
                  <Button variant="ghost" className="font-semibold text-[#6b47dc] hover:bg-purple-50 hover:text-[#6b47dc]">Log in</Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-[#ff5722] hover:bg-[#e64a19] text-white font-semibold btn-shine shadow-md hover:shadow-lg">Sign up</Button>
                </Link>
              </>
            )}

            <button className="lg:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-purple-100 pt-3 space-y-1">
            <div className="relative mb-3">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input placeholder="Search courses" className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm" />
            </div>
            <Link to="/courses" className="block px-3 py-2 rounded-lg font-semibold text-gray-700 hover:bg-purple-50">All Courses</Link>
            <Link to="/dod-8140-certification-training" className="block px-3 py-2 rounded-lg font-semibold text-gray-700 hover:bg-purple-50">DoD 8140 Training</Link>
            {cats.slice(0, 6).map(c => (
              <Link key={c.id} to={`/courses?category=${c.id}`} className="block px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-purple-50">{c.name}</Link>
            ))}
            <Link to="/contact" className="block px-3 py-2 rounded-lg font-semibold text-gray-700 hover:bg-purple-50">Contact</Link>
          </div>
        )}
      </div>
    </header>
  );
}
